import { useEffect, useState } from "react";

import { Form, Input, InputNumber, Modal, Select, Radio } from "antd";

import useExcursions from "../hooks/useExcursions";
import useUsers from "../hooks/useUsers";
import usePassengers from "../hooks/usePassengers";

function BusPaymentFormModal({
  isOpen,
  paymentToEdit,
  onSuccess,
  onCancel,
  selectedBus,
}) {
  const { loading } = useExcursions();
  const { users, getUsers } = useUsers();
  const { paymentMethods, getPaymentMethods } = usePassengers();
  const [form] = Form.useForm();
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    // Cargar usuarios OFFICE_WORKER y métodos de pago
    getUsers({ user_type: "OFFICE_WORKER" });
    getPaymentMethods();

    if (paymentToEdit) {
      const isCredit = paymentToEdit.amount > 0;
      form.setFieldsValue({
        ...paymentToEdit,
        amount: Math.abs(paymentToEdit.amount),
        transactionType: isCredit ? "CREDIT" : "DEBIT",
        user: paymentToEdit.user,
        payment_method: paymentToEdit.payment_method || paymentToEdit.transfer,
      });
      setSelectedUser(paymentToEdit.user);
    } else {
      form.resetFields();
      form.setFieldsValue({
        transactionType: "DEBIT",
        user: selectedBus?.guide?.id // Seleccionar el guía por defecto
      });
      setSelectedUser(selectedBus?.guide?.id);
    }
  }, [isOpen, paymentToEdit, form]);

  const handleSave = () => {
    form.validateFields().then(async (values) => {
      const { amount, transactionType, ...restValues } = values;
      const finalAmount =
        transactionType === "DEBIT" ? -Math.abs(amount) : Math.abs(amount);

      form.resetFields();
      form.setFieldsValue({ transactionType: "DEBIT" });

      onSuccess({
        ...restValues,
        id: paymentToEdit?.id,
        scheduleExcursionBus: selectedBus?.id,
        amount: finalAmount,
      });
    });
  };

  return (
    <>
      <Modal
        title="Editar Gasto"
        okText={paymentToEdit ? "Editar Gasto" : "Crear Gasto"}
        confirmLoading={paymentToEdit ? loading.put : loading.post}
        open={isOpen}
        onCancel={onCancel}
        onOk={handleSave}
        afterClose={() => {
          form.resetFields();
          form.setFieldsValue({
            transactionType: "DEBIT",
            user: selectedBus?.guide?.id
          });
          setSelectedUser(selectedBus?.guide?.id);
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="type"
            label="Tipo"
            rules={[
              { required: true, message: "Por favor seleccione el tipo" },
            ]}
            style={{ marginBottom: "8px" }}
          >
            <Select>
              <Select.Option value="GUIDE_PAYMENT">Paga Guiada</Select.Option>
              <Select.Option value="SELLER_TAXI">Gasto de taxi</Select.Option>
              <Select.Option value="TRANSPORT_PAYMENT">
                Paga Transportista
              </Select.Option>
              <Select.Option value="FIXED_COST_PER_PASSENGER">
                Gasto Fijo de la Excursion por pasajero
              </Select.Option>
              <Select.Option value="ADDITIONAL_EXPENSES">
                Gastos adicionales
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="user"
            label="Usuario que realizó el gasto"
            rules={[
              { required: true, message: "Por favor seleccione el usuario" },
            ]}
            style={{ marginBottom: "8px" }}
          >
            <Select
              onChange={(value) => {
                setSelectedUser(value);
                // Si cambia de OFFICE_WORKER a guía, limpiar el método de pago
                if (value === selectedBus?.guide?.id) {
                  form.setFieldsValue({ payment_method: undefined });
                }
              }}
              placeholder="Seleccione el usuario"
            >
              {/* Guía del micro */}
              {selectedBus?.guide && (
                <Select.Option key={`guide-${selectedBus.guide.id}`} value={selectedBus.guide.id}>
                  {selectedBus.guide.firstName} {selectedBus.guide.lastName} (Guía)
                </Select.Option>
              )}
              {/* Usuarios OFFICE_WORKER - filtrar el guía para evitar duplicados */}
              {users
                ?.filter((user) => user.id !== selectedBus?.guide?.id)
                .map((user) => (
                  <Select.Option key={`office-${user.id}`} value={user.id}>
                    {user.firstName} {user.lastName} (Oficina)
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          {/* Campo de método de pago - solo visible si es OFFICE_WORKER */}
          {selectedUser && selectedUser !== selectedBus?.guide?.id && (
            <Form.Item
              name="payment_method"
              label="Método de pago"
              rules={[
                {
                  required: true,
                  message: "Por favor seleccione el método de pago",
                },
              ]}
              style={{ marginBottom: "8px" }}
            >
              <Select placeholder="Seleccione el método de pago">
                {paymentMethods?.map((method) => (
                  <Select.Option key={method.id} value={method.id}>
                    {method.name} ({method.box})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="Tipo de Transacción"
            name="transactionType"
            initialValue="DEBIT"
            rules={[
              {
                required: true,
                message: "Por favor seleccione el tipo de transacción",
              },
            ]}
            style={{ marginBottom: "8px" }}
          >
            <Radio.Group>
              <Radio.Button value="CREDIT">Crédito</Radio.Button>
              <Radio.Button value="DEBIT">Débito</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Monto"
            rules={[{ required: true, message: "Por favor ingrese el monto" }]}
            style={{ marginBottom: "8px" }}
          >
            <InputNumber
              placeholder="Monto"
              className="w-full"
              min={0}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Descripción"
            style={{ marginBottom: "8px" }}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default BusPaymentFormModal;
